/* eslint-disable complexity */
import getConfig from 'next/config';
import {
  IconIllustrationSmallChildCare,
  IconIllustrationSmallSafetyCheck,
  IconIllustrationSmallSeniorCare,
  IconIllustrationSmallPolaroids,
  IconIllustrationSmallHeart,
} from '@care/react-icons';
import { Grid, Button, Box, Typography, Theme, useMediaQuery } from '@mui/material';
import { ServiceIdForMember } from '@/constants';
import { AmplitudeEvent, trackAmplitudeEvent } from '@/lib/analyticsHelper';

const {
  publicRuntimeConfig: { NEXT_PUBLIC_APP_URL },
} = getConfig();

interface HowItWorksProps {
  serviceIdForMember?: string;
  segmentNameSingularLowerCase?: string;
  segmentNamePluralLowerCase?: string;
  isCostGuidePage?: boolean;
  segmentationUrl?: string;
  isCostGuideLandingPage?: boolean;
}

const HowItWorks = ({
  serviceIdForMember,
  segmentNamePluralLowerCase,
  segmentNameSingularLowerCase,
  isCostGuidePage = false,
  segmentationUrl,
  isCostGuideLandingPage = false,
}: HowItWorksProps) => {
  const isMobileOrSmaller = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const costGuideHeadlineSubstring = isCostGuideLandingPage
    ? 'caregivers'
    : segmentNamePluralLowerCase;
  const costGuideHeadline = `Find ${costGuideHeadlineSubstring} near you that you'll love`;
  const costGuideCtaText = isCostGuideLandingPage
    ? 'Join free to get started'
    : `View ${segmentNameSingularLowerCase} profiles`;

  const isChildCareLocation = serviceIdForMember === ServiceIdForMember.CHILD_CARE_LOCATION;

  const handleCtaBtn = () => {
    const amplitudeData = {
      cta_text: costGuideCtaText,
      cta_type: 'button',
      url: `${NEXT_PUBLIC_APP_URL}${segmentationUrl}`,
      url_path: segmentationUrl,
    };

    trackAmplitudeEvent(AmplitudeEvent.ctaInteracted, amplitudeData);
  };

  const hiwIconOne = isChildCareLocation ? (
    <IconIllustrationSmallChildCare
      sx={{
        width: '65px',
        height: '65px',
        marginTop: -1.5,
        marginLeft: -0.6,
      }}
    />
  ) : (
    <IconIllustrationSmallPolaroids
      sx={{
        width: '65px',
        height: '65px',
        marginTop: -1.5,
        marginLeft: -0.6,
      }}
    />
  );

  const hiwIconThree = isChildCareLocation ? (
    <IconIllustrationSmallSeniorCare
      sx={{
        width: '65px',
        height: '65px',
        marginTop: -1.5,
        marginLeft: -0.6,
      }}
    />
  ) : (
    <IconIllustrationSmallHeart
      sx={{
        width: '65px',
        height: '65px',
        marginTop: -1.5,
        marginLeft: -0.6,
      }}
    />
  );

  const hiwContentSubStringOne = isCostGuideLandingPage
    ? ''
    : `${' '}${segmentNameSingularLowerCase}`;
  const hiwContentSubStringTwo = isCostGuideLandingPage ? 'caregivers' : segmentNamePluralLowerCase;

  const hiwIconThreeHeadline = isChildCareLocation
    ? `Compare${hiwContentSubStringOne} and tour the ones that best fit your family`
    : `Compare${hiwContentSubStringOne} options and choose the best fit`;

  const howItWorksList = [
    {
      icon: hiwIconOne,
      content: `Share details about your${hiwContentSubStringOne} needs`,
    },
    {
      icon: (
        <IconIllustrationSmallSafetyCheck
          sx={{
            width: '65px',
            height: '65px',
            marginTop: -1.5,
            marginLeft: -0.6,
          }}
        />
      ),
      content: `Match with highly rated local ${hiwContentSubStringTwo} near you`,
    },
    {
      icon: hiwIconThree,
      content: hiwIconThreeHeadline,
    },
  ];

  const howItWorksContent = howItWorksList.map((item, idx) => {
    return (
      <Box
        className={isCostGuidePage || isCostGuideLandingPage ? 'hiw-cost-guide' : 'hiw'}
        sx={{
          display: { xs: 'flex', sm: 'inline-block' },
          alignItems: 'center',
          alignContent: 'center',
          flexWrap: 'wrap',
          position: 'relative',
          pb: { xs: 3.7, sm: 3 },
          width: { sm: '240px' },

          '&.hiw-cost-guide:not(:last-child)': {
            '&:after': {
              content: '""',
            },
          },

          '&.hiw:not(:last-child)': {
            '&:after': {
              content: { xs: '"\u22EE"', sm: '"......"' },
            },
          },

          '&:not(:last-child)': {
            '&:after': {
              color: 'brand.main',
              display: 'block',
              fontSize: 'h1.fontSize',
              left: { xs: '22px', sm: '90%', md: '95%', lg: '125%' },
              position: 'absolute',
              top: { xs: '70%', sm: '0' },
              letterSpacing: { sm: '3px' },
            },
          },
        }}
        key={`${item.content}-${idx + 1}`}>
        <Box
          sx={{
            width: { xs: '50px', sm: '100%' },
            height: '50px',
            mr: 3,
            textAlign: 'center',
            alignSelf: 'flex-start',
            pt: { xs: 1, sm: 0 },
            mx: { sm: 0 },
            mt: { sm: 0 },
            mb: { sm: 1 },
          }}>
          {item.icon}
        </Box>
        <Box maxWidth={{ xs: '245px', sm: '100%' }} flex={1}>
          <Typography variant="body2" mb={0.5}>
            {item.content}
          </Typography>
        </Box>
      </Box>
    );
  });

  return (
    <Grid
      className={isCostGuideLandingPage ? 'landing-page' : 'local-page'}
      sx={{
        '&.landing-page': {
          backgroundColor: 'background.default',
        },
        '&.local-page': {
          background: 'care.grey.100',
        },
      }}>
      <Grid item xs={12}>
        <Box pt={3} px={2} pb={0} sx={{ textAlign: { sm: 'center' } }}>
          {!isCostGuidePage && !isCostGuideLandingPage && (
            <Typography mb={{ xs: 1.5, sm: 3 }} variant="h2" align="center">
              How it works
            </Typography>
          )}
          {(isCostGuidePage || isCostGuideLandingPage) && (
            <Typography
              sx={(theme: Theme) => ({
                fontSize: 'h2.fontSize',
                pb: 2,

                [theme.breakpoints.up('md')]: {
                  padding: theme.spacing(1, 10, 4, 10),
                  fontSize: 'h1.fontSize',
                },
              })}
              variant="h2"
              align="center">
              {costGuideHeadline}
            </Typography>
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: { sm: 'space-evenly' },
              pl: 1,
            }}>
            {howItWorksContent}
          </Box>
          {(isCostGuidePage || isCostGuideLandingPage) && (
            <Box display="flex" justifyContent="center" alignItems="center" pb={5}>
              <Box width={{ xs: '270px', sm: '315px', md: '362px' }}>
                <Button
                  sx={{
                    mt: { xs: 2, md: 1 },
                  }}
                  color="primary"
                  component="a"
                  fullWidth
                  onClick={handleCtaBtn}
                  href={segmentationUrl}
                  size={isMobileOrSmaller ? 'medium' : 'large'}
                  variant="contained"
                  rel={isCostGuideLandingPage ? 'nofollow' : ''}>
                  {costGuideCtaText}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export default HowItWorks;
